/**
 * Remove Friend Dialog Component
 * Coláiste Lurgan Management System - Student friendship removal confirmation
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { useRemoveFriendship } from '../../hooks/useStudents';
import { AlertTriangle, Loader2, UserMinus, X } from 'lucide-react';

export function RemoveFriendDialog({ open, onOpenChange, friendship, friendName, alsoUnlinkedNames = [] }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const removeFriendshipMutation = useRemoveFriendship({
    onSuccess: () => {
      // Invalidate friendship queries to force refetch
      queryClient.invalidateQueries(['friendships']);

      toast({
        title: t('friendships.removeSuccess'),
        description: t('friendships.removeSuccessDescription', { name: friendName }),
        variant: 'success',
      });
      onOpenChange(false);
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.detail ||
                          error.response?.data?.message ||
                          t('friendships.removeError');
      toast({
        title: t('friendships.removeErrorTitle'),
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  const handleRemove = () => {
    if (!friendship?.uuid) return;
    removeFriendshipMutation.mutate(friendship.uuid);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <AlertTriangle className="h-5 w-5" />
            {t('friendships.removeFriendTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('friendships.removeFriendDescription', { name: friendName })}
          </DialogDescription>
        </DialogHeader>

        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <p className="text-sm text-amber-800 dark:text-amber-300">
            <strong>{t('friendships.warningLabel')}</strong> {t('friendships.removeWarning')}
          </p>
          {alsoUnlinkedNames.length > 0 && (
            <p className="text-sm text-amber-800 dark:text-amber-300 mt-2">
              {t('friendships.removeAlsoUnlinked', { names: alsoUnlinkedNames.join(', ') })}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={removeFriendshipMutation.isPending}
          >
            <X className="h-4 w-4 mr-2" />
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleRemove}
            disabled={removeFriendshipMutation.isPending}
          >
            {removeFriendshipMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('friendships.removing')}
              </>
            ) : (
              <>
                <UserMinus className="h-4 w-4 mr-2" />
                {t('friendships.removeFriend')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RemoveFriendDialog;
